import { useState } from 'react'
import { Plus } from 'lucide-react'
import type { ZebraTemplate } from '@/api/labelPrintingApi'
import { useZebraTemplates } from '@/hooks/useLabelPrinting'
import {
  effectiveFromText,
  placeholderCountText,
  sizeAndResolution,
  zebraShownVersion,
  zebraTemplateStatus,
  zebraVersionText,
} from '@/lib/labelPrinting'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { Button } from '@/components/ui/button'
import { TableCell } from '@/components/ui/table'
import { ZebraTemplateDialog } from './ZebraTemplateDialog'
import { ZebraTemplateFormDialog } from './ZebraTemplateFormDialog'
import { ListSection, ListTable, NameCell, StatusChip } from './shared'

const TABLE_ID = 'label-printing-zebra-templates'

const COLUMNS: TableColumnDef[] = [
  { id: 'name', header: 'Name', locked: true, defaultWidth: 200, sortable: true },
  { id: 'placeholders', header: 'Placeholders', defaultWidth: 140, sortable: true },
  { id: 'sizeResolution', header: 'Size and resolution', defaultWidth: 200, sortable: true },
  { id: 'version', header: 'Version', defaultWidth: 180, sortable: true },
  { id: 'effectiveFrom', header: 'Effective from', defaultWidth: 150, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 130, sortable: true },
]

const placeholderCount = (t: ZebraTemplate) => zebraShownVersion(t)?.placeholders.length ?? 0

const SORT: SortAccessors<ZebraTemplate> = {
  name: t => t.name,
  placeholders: placeholderCount,
  sizeResolution: t => sizeAndResolution(t.labelStock, t.resolution),
  version: t => zebraShownVersion(t)?.version ?? null,
  // `YYYY-MM-DD` sorts as text.
  effectiveFrom: t => zebraShownVersion(t)?.effectiveDate ?? null,
  status: t => zebraTemplateStatus(t).label,
}

/** Which dialog is open: a template read, a new one, or a change to one. */
type Open = { mode: 'view'; id: string } | { mode: 'new' } | { mode: 'edit'; id: string }

/**
 * Zebra label designs (z8nrz7evkc). Anyone who can open the page can read a
 * template and its versions. Managers add templates and change them; every
 * change is a draft that printers use only once it is approved.
 */
export function ZebraTemplatesList({
  canManage,
  onNotice,
}: {
  canManage: boolean
  onNotice: (message: string) => void
}) {
  const query = useZebraTemplates()
  const [open, setOpen] = useState<Open | null>(null)
  // Read from the list, so an open dialog shows the saved row after every refetch.
  const openId = open && open.mode !== 'new' ? open.id : null
  const current = openId ? query.data?.find(t => t.id === openId) : undefined

  function done(message: string) {
    setOpen(null)
    onNotice(message)
  }

  return (
    <>
      <ListSection
        title='Zebra Templates'
        blurb='One row per Zebra label layout, for supplies, equipment, reagents and kits. Smart CGT fills in the blanks and sends the finished label. A new or changed layout is saved as a draft, and printers use it only after it is approved.'
        aside={
          canManage && (
            <Button onClick={() => setOpen({ mode: 'new' })}>
              <Plus className='mr-2 h-4 w-4' />
              Add template
            </Button>
          )
        }
        query={query}
        noun='templates'
      >
        {rows => (
          <ListTable
            tableId={TABLE_ID}
            columns={COLUMNS}
            sort={SORT}
            rows={rows}
            cells={t => {
              const shown = zebraShownVersion(t)
              return {
                name: (
                  <TableCell>
                    <NameCell name={t.name} onOpen={() => setOpen({ mode: 'view', id: t.id })} />
                  </TableCell>
                ),
                placeholders: <TableCell>{placeholderCountText(placeholderCount(t))}</TableCell>,
                sizeResolution: <TableCell>{sizeAndResolution(t.labelStock, t.resolution)}</TableCell>,
                version: <TableCell>{zebraVersionText(t)}</TableCell>,
                effectiveFrom: (
                  <TableCell className='text-muted-foreground'>
                    {shown ? effectiveFromText(shown.effectiveDate) : '—'}
                  </TableCell>
                ),
                status: (
                  <TableCell>
                    <StatusChip {...zebraTemplateStatus(t)} />
                  </TableCell>
                ),
              }
            }}
          />
        )}
      </ListSection>

      {open?.mode === 'view' && current && (
        <ZebraTemplateDialog
          key={current.id}
          template={current}
          canManage={canManage}
          onEdit={() => setOpen({ mode: 'edit', id: current.id })}
          onClose={() => setOpen(null)}
          onDone={done}
        />
      )}
      {(open?.mode === 'new' || (open?.mode === 'edit' && current)) && (
        <ZebraTemplateFormDialog
          key={current?.id ?? 'new'}
          template={current}
          onClose={() => setOpen(current ? { mode: 'view', id: current.id } : null)}
          onDone={done}
        />
      )}
    </>
  )
}
